import { memo, useEffect, useState } from "react";
import { Tooltip } from "@/components/shared/Tooltip";
import { twMerge } from "tailwind-merge";
import { PositionStatus } from "./position-table-row.utils";

const textMap: Partial<Record<PositionStatus, string>> = {
    [PositionStatus.UNLOCKING]: "Unlocking",
    [PositionStatus.UNLOCKED]: "Unlocked",
};

const baseBadgeClassName = "border-[1px] text-[11px] leading-[11px] font-medium max-h-5 px-1 py-0.5 rounded-[4px]";
const statusClassMap: Partial<Record<PositionStatus, string>> = {
    [PositionStatus.UNLOCKING]: "bg-secondary/5 text-secondary border-secondary",
    [PositionStatus.UNLOCKED]: "bg-primary/5 text-primary  border-primary/20",
};

type PositionRowStatusBadgeProps = {
    status: PositionStatus;
    unlockAt: number | string | Date | null;
};

const toTimestamp = (value: PositionRowStatusBadgeProps["unlockAt"]): number | null => {
    if (value == null) return null;
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : null;
};

const formatCountdown = (remainingMs: number): string => {
    const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
    const weeks = Math.floor(totalSeconds / (7 * 86400));
    const days = Math.floor((totalSeconds % (7 * 86400)) / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    const parts = [
        [weeks, "weeks"],
        [days, "days"],
        [hours, "hours"],
        [minutes, "minutes"],
    ] as const;

    const visibleParts = parts.filter(([value]) => value > 0).map(([value, label]) => `${value} ${label}`);

    return visibleParts.length > 0 ? visibleParts.join(", ") : "0 minutes";
};

export const PositionRowStatusBadge = memo(function PositionRowStatusBadge({
    status,
    unlockAt,
}: PositionRowStatusBadgeProps) {
    const isUnlocking = status === PositionStatus.UNLOCKING;
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!isUnlocking || unlockAt == null) return;

        const intervalId = window.setInterval(() => {
            setNow(Date.now());
        }, 1000);

        return () => window.clearInterval(intervalId);
    }, [isUnlocking, unlockAt]);

    const unlockAtTs = toTimestamp(unlockAt);
    const countdown = isUnlocking && unlockAtTs != null ? formatCountdown(unlockAtTs - now) : null;

    const text = status != null ? textMap[status] : undefined;
    if (!text) return null;

    const tooltip =
        isUnlocking && countdown ? (
            <div className="flex flex-col gap-2 p-1">
                <span className="text-[10px] leading-[7px] text-portfolio-positions-table-tooltip-label">
                    Remaining exit time:
                </span>
                <span className="text-[10px] leading-[7px]">{countdown}</span>
            </div>
        ) : undefined;

    const statusClassName = statusClassMap[status];
    const badge = <span className={twMerge(baseBadgeClassName, statusClassName, "select-none")}>{text}</span>;

    if (!tooltip) return badge;

    return <Tooltip title={tooltip}>{badge}</Tooltip>;
});

PositionRowStatusBadge.displayName = "PositionRowStatusBadge";
